import type {
  AdminListItem,
  AuditLogItem,
  DashboardRecent,
  DashboardSummary,
  FinanceSummary,
  GlobalSearchResult,
  GrantSubscriptionResponse,
  PaginatedAuditLog,
  PaginatedPayments,
  PaginatedPromoRedemptions,
  PaginatedUsers,
  PaginatedVpnKeys,
  PaymentDetail,
  PromoDetail,
  PromoListItem,
  ServerHealth,
  ServerListItem,
  SettingsOverview,
  SyncResult,
  UserDetail,
  VpnKeyConfigResponse,
  VpnKeyDetail,
} from './types';

const API_PREFIX = import.meta.env.VITE_API_PREFIX || '/vpnbotpanel/api';

export interface ApiError {
  code: string;
  message: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: ApiError;
}

export interface UserProfile {
  telegram_user_id: number;
  role: string;
  username?: string;
  first_name?: string;
  photo_url?: string;
}

export interface AuthMeResponse {
  authenticated: boolean;
  user?: UserProfile;
  csrf_token?: string;
}

export interface HealthStatus {
  status: string;
  database: string;
  redis: string;
  version: string;
}

export interface PublicConfig {
  admin_panel_url: string;
  user_portal_url: string;
  app_base_path: string;
  csrf_header_name: string;
  bot_username: string;
}

export type {
  AdminListItem,
  AuditLogItem,
  DashboardRecent,
  DashboardSummary,
  FinanceSummary,
  GlobalSearchResult,
  GrantSubscriptionResponse,
  PaginatedAuditLog,
  PaginatedPayments,
  PaginatedPromoRedemptions,
  PaginatedUsers,
  PaginatedVpnKeys,
  PaymentDetail,
  PromoDetail,
  PromoListItem,
  ServerHealth,
  ServerListItem,
  SettingsOverview,
  SyncResult,
  UserDetail,
  VpnKeyConfigResponse,
  VpnKeyDetail,
};

let csrfToken: string | null = null;

export function getCsrfToken(): string | null {
  return csrfToken;
}

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<ApiResponse<T>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (csrfToken && options.method && options.method !== 'GET') {
    headers['X-CSRF-Token'] = csrfToken;
  }

  const response = await fetch(`${API_PREFIX}${path}`, {
    ...options,
    headers,
    credentials: 'include',
  });

  if (!response.ok && response.status === 401) {
    return { success: false, error: { code: 'not_authenticated', message: 'Unauthorized' } };
  }

  return (await response.json()) as ApiResponse<T>;
}

function qs(params: Record<string, string | number | undefined | null>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export const api = {
  getConfig: () => request<PublicConfig>('/auth/config'),
  getMe: () => request<AuthMeResponse>('/auth/me'),
  loginTelegram: (payload: Record<string, unknown>) =>
    request<AuthMeResponse>('/auth/telegram', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  logout: () => request<{ logged_out: boolean }>('/auth/logout', { method: 'POST' }),
  getHealth: () => request<HealthStatus>('/health'),

  getDashboardSummary: () => request<DashboardSummary>('/dashboard/summary'),
  getDashboardRecent: (limit = 10) =>
    request<DashboardRecent>(`/dashboard/recent${qs({ limit })}`),

  listUsers: (params: {
    page?: number;
    page_size?: number;
    q?: string;
    subscription_status?: string;
    tier?: string;
    language?: string;
  }) => request<PaginatedUsers>(`/users${qs(params)}`),

  getUser: (userId: number) => request<UserDetail>(`/users/${userId}`),

  globalSearch: (q: string, limit = 20) =>
    request<GlobalSearchResult>(`/search${qs({ q, limit })}`),

  listVpnKeys: (params: {
    page?: number;
    page_size?: number;
    user_id?: number;
    server_code?: string;
    status?: string;
    q?: string;
  }) => request<PaginatedVpnKeys>(`/vpn/keys${qs(params)}`),

  getVpnKey: (keyId: number, live = true) =>
    request<VpnKeyDetail>(`/vpn/keys/${keyId}${qs({ live: live ? 1 : 0 })}`),

  getVpnKeyConfig: (keyId: number) =>
    request<VpnKeyConfigResponse>(`/vpn/keys/${keyId}/config`),

  createVpnKey: (payload: { user_id: number; server_code: string; force?: boolean }) =>
    request<{ key_id: number; client_id: string; server_code: string }>('/vpn/keys', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  deleteVpnKey: (keyId: number) =>
    request<{ deleted: boolean; key_id: number }>(`/vpn/keys/${keyId}`, { method: 'DELETE' }),

  listServers: () => request<ServerListItem[]>('/servers'),

  getServerHealth: (code: string) => request<ServerHealth>(`/servers/${encodeURIComponent(code)}/health`),

  reloadServersConfig: () =>
    request<{ reloaded: boolean; servers_count: number }>('/servers/reload-config', {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  syncServerReplicas: (code: string) =>
    request<SyncResult>(`/servers/${encodeURIComponent(code)}/sync`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  getFinanceSummary: () => request<FinanceSummary>('/finance/summary'),

  grantSubscription: (payload: { user_id: number; tier: string; days: number }) =>
    request<GrantSubscriptionResponse>('/finance/grant-subscription', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  listPayments: (params: {
    page?: number;
    page_size?: number;
    user_id?: number;
    status?: string;
    tier?: string;
    q?: string;
  }) => request<PaginatedPayments>(`/payments${qs(params)}`),

  getPayment: (paymentId: string) =>
    request<PaymentDetail>(`/payments/${encodeURIComponent(paymentId)}`),

  updatePaymentStatus: (paymentId: string, status: string) =>
    request<PaymentDetail>(`/payments/${encodeURIComponent(paymentId)}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),

  listPromos: () => request<PromoListItem[]>('/promos'),

  getPromo: (code: string) => request<PromoDetail>(`/promos/${encodeURIComponent(code)}`),

  getPromoRedemptions: (code: string, page = 1) =>
    request<PaginatedPromoRedemptions>(
      `/promos/${encodeURIComponent(code)}/redemptions${qs({ page })}`,
    ),

  upsertPromo: (payload: {
    code: string;
    bonus_days: number;
    subscription_tier?: string;
    max_uses?: number;
    active?: boolean;
  }) =>
    request<PromoDetail>('/promos', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  setPromoActive: (code: string, active: boolean) =>
    request<PromoDetail>(`/promos/${encodeURIComponent(code)}/active`, {
      method: 'PATCH',
      body: JSON.stringify({ active }),
    }),

  getSettingsOverview: () => request<SettingsOverview>('/settings/overview'),

  listAuditLog: (params: {
    page?: number;
    page_size?: number;
    action?: string;
    actor_telegram_id?: number;
    resource_type?: string;
    q?: string;
  }) => request<PaginatedAuditLog>(`/audit${qs(params)}`),

  listAdmins: () => request<AdminListItem[]>('/admins'),

  upsertAdmin: (payload: { telegram_user_id: number; role: string }) =>
    request<AdminListItem>('/admins', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  updateAdmin: (telegramUserId: number, payload: { role?: string; is_active?: boolean }) =>
    request<AdminListItem>(`/admins/${telegramUserId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),
};
